/**
 * The old canvas as the workspace view (Part 06 decision 2). Each method is
 * moved from the canvas call site it replaces; Phase 7 deletes this file
 * with the canvas.
 */
import { useMemo } from "react";
import type { Node, Viewport } from "@xyflow/react";
import { useShallow } from "zustand/react/shallow";
import type { FrameScreenRect } from "@/components/cursors/frame-cursor-geometry";
import {
  CANVAS_LIVE_VIEWPORT_EVENT,
  getLiveCanvasViewport,
} from "@/lib/canvas-live-viewport";
import { suppressNextAutoFitForPresentedFrame } from "@/lib/canvas-viewport-autofit-suppression";
import { openInFocus } from "@/lib/focus-frame-activation";
import type { FrameData } from "@/lib/frame-data";
import { frameNodeSize } from "@/lib/frame-node-geometry";
import { exitPresentationMode } from "@/lib/presentation-mode";
import { unhideCanvasFrames } from "@/lib/reveal-canvas-frames";
import type {
  WorkspacePoint,
  WorkspaceTransform,
  WorkspaceViewAdapter,
} from "@/lib/workspace-view-adapter";
import { useCanvasStore } from "@/stores/canvas-store";
import { useCanvasTimePageStore } from "@/stores/canvas-time-page-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useProjectStore } from "@/stores/project-store";
import {
  canvasFramePositions,
  canvasViewCenter,
} from "./canvas-frame-positions";
import { settleCanvasFrames } from "./canvas-settle-frames";
import { findCanvasTimePageIndex } from "./canvas-time-pages";

export function frameScreenRectFromNode(
  node: Node<FrameData>,
  viewport: Viewport,
): FrameScreenRect {
  const zoom = viewport.zoom || 1;
  const { width, height } = frameNodeSize(node);
  return {
    left: node.position.x * zoom + viewport.x,
    top: node.position.y * zoom + viewport.y,
    width: width * zoom,
    height: height * zoom,
  };
}

/** The live viewport while React Flow pans, else the persisted one. */
function canvasViewport(): Viewport {
  const scopeId = useProjectStore.getState().currentSpace?.id ?? null;
  return getLiveCanvasViewport(scopeId) ?? useCanvasStore.getState().viewport;
}

function canvasElement(): HTMLElement | null {
  return document.querySelector<HTMLElement>("[data-canvas-viewport]");
}

function withContainerOrigin(viewport: Viewport): WorkspaceTransform {
  const origin = canvasElement()?.getBoundingClientRect();
  return {
    x: viewport.x + (origin?.left ?? 0),
    y: viewport.y + (origin?.top ?? 0),
    zoom: viewport.zoom || 1,
  };
}

/** The canvas's client rect while it shows, else null. */
function visibleCanvasBounds(): DOMRect | null {
  const canvas = canvasElement();
  if (!canvas) return null;
  const rect = canvas.getBoundingClientRect();
  if (
    rect.width <= 0 ||
    rect.height <= 0 ||
    rect.right <= 0 ||
    rect.bottom <= 0 ||
    rect.left >= window.innerWidth ||
    rect.top >= window.innerHeight
  )
    return null;
  for (
    let element: HTMLElement | null = canvas;
    element;
    element = element.parentElement
  ) {
    const style = getComputedStyle(element);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.opacity === "0"
    )
      return null;
  }
  return rect;
}

/**
 * Unhide the frames, open the newest one's time page and fit the frames on
 * it, once React Flow has drawn them. Another presentation is exited first.
 */
function fitFramesIntoView(frameIds: readonly string[]): void {
  const existing = useCanvasStore
    .getState()
    .nodes.filter((node) => frameIds.includes(node.id));
  if (existing.length === 0) return;
  exitPresentationMode({ restoreViewport: false });
  unhideCanvasFrames(existing.map((node) => node.id));
  const newest = [...existing].sort((a, b) =>
    String(b.data.createdAt).localeCompare(String(a.data.createdAt)),
  )[0]!;
  const visibleNodes = useCanvasStore.getState().nodes;
  const spaceId = useProjectStore.getState().currentSpace?.id;
  const pageIndex = findCanvasTimePageIndex(visibleNodes, newest.id, spaceId);
  if (pageIndex !== null) {
    useCanvasTimePageStore.getState().setActivePageIndex(pageIndex);
  }
  const onPage = existing
    .filter(
      (node) =>
        pageIndex === null ||
        findCanvasTimePageIndex(visibleNodes, node.id, spaceId) === pageIndex,
    )
    .map((node) => node.id);
  const fit = () => useCanvasStore.getState().fitViewNodes?.(onPage);
  if (typeof requestAnimationFrame === "function") requestAnimationFrame(fit);
  else setTimeout(fit, 0);
}

export const canvasViewAdapter: WorkspaceViewAdapter = {
  kind: "canvas",

  frameRect(frameId) {
    const node = useCanvasStore
      .getState()
      .nodes.find((candidate) => candidate.id === frameId);
    return node ? frameScreenRectFromNode(node, canvasViewport()) : null;
  },

  subscribe(listener) {
    const unsubscribe = useCanvasStore.subscribe((state, previous) => {
      if (
        state.viewport !== previous.viewport ||
        state.nodes !== previous.nodes
      )
        listener();
    });
    window.addEventListener(CANVAS_LIVE_VIEWPORT_EVENT, listener);
    return () => {
      unsubscribe();
      window.removeEventListener(CANVAS_LIVE_VIEWPORT_EVENT, listener);
    };
  },

  // The canvas keeps its nodes where the user left them.
  bringIntoView: () => Promise.resolve(true),

  transform: () => withContainerOrigin(canvasViewport()),

  useTransform() {
    // Equal viewports do not re-render.
    const viewport = useCanvasStore(useShallow((state) => state.viewport));
    return useMemo(() => withContainerOrigin(viewport), [viewport]);
  },

  toWorkspacePoint(client: WorkspacePoint) {
    const toFlow = useCanvasStore.getState().screenToFlowPosition;
    if (toFlow) return toFlow(client);
    const { x, y, zoom } = withContainerOrigin(canvasViewport());
    return { x: (client.x - x) / zoom, y: (client.y - y) / zoom };
  },

  settleFrames: settleCanvasFrames,
  async showOverview() {
    // Loaded on use: the presentation module wires desktop-mode reactions
    // when it is imported.
    const { showAllFramesTidied } = await import("@/lib/canvas-presentation");
    showAllFramesTidied();
  },
  // The caller settles every frame at once; a single new node's auto-fit
  // would race that.
  prepareFrameArrival: suppressNextAutoFitForPresentedFrame,

  viewportCenterHint: canvasViewCenter,
  framePositions: canvasFramePositions,

  revealFrames(frameIds) {
    // Focus ignores fit requests, so an open focus takes the frames in place.
    if (usePresentationStore.getState().presentation?.variant === "focus") {
      openInFocus(frameIds, { origin: "navigate" });
      return;
    }
    fitFramesIntoView(frameIds);
  },

  visibleConversationIds(conversationIds) {
    const bounds = visibleCanvasBounds();
    const viewport = canvasViewport();
    if (!bounds || !Number.isFinite(viewport.zoom) || viewport.zoom <= 0)
      return [];
    const left = Math.max(0, bounds.left);
    const top = Math.max(0, bounds.top);
    const right = Math.min(window.innerWidth, bounds.right);
    const bottom = Math.min(window.innerHeight, bounds.bottom);
    if (right <= left || bottom <= top) return [];
    const nodesById = new Map(
      useCanvasStore.getState().nodes.map((node) => [node.id, node]),
    );
    return conversationIds.filter((frameId) => {
      const node = nodesById.get(frameId);
      if (!node || node.hidden) return false;
      const rect = frameScreenRectFromNode(node, viewport);
      const x = bounds.left + rect.left;
      const y = bounds.top + rect.top;
      return (
        x < right &&
        y < bottom &&
        x + rect.width > left &&
        y + rect.height > top
      );
    });
  },
};
